// Entrada a referencias del empty state de "Crear desde cero" — SOLO MOBILE.
//
// La grilla de escritorio con las tres cards ("Añadir producto" / "Añadir
// modelo" / "Subir imagen") se quitó: en desktop el empty state va limpio y
// esas tres acciones siguen estando en el menú "+" del composer, que es de
// donde salieron (ADR-2026-09-08-asistente-v2-sin-aura-ni-cards).
//
// Lo que queda es mobile: UNA card que abre el sheet de referencias, más las
// miniaturas de lo ya añadido. No es la misma pieza que las cards de desktop
// (label distinto, y además muestra el estado), así que sobrevive.

import React from 'react';
import { Package, User, Image as ImageIcon, X } from 'lucide-react';
import CachedImg from '../../../../components/CachedImg';
import { palabraDeModeloActual } from '../../../../lib/nombreDeModelo';

/** Una referencia ya añadida, lista para mostrar como miniatura. */
export interface ReferenciaThumb {
  id: string;
  url: string | null;
  label: string;
}

/** Alto de la card de referencias en mobile, compartido por las TRES piezas:
 *  la card grande ("Añadí referencias"), la compacta ("Añadir más") y las
 *  miniaturas. Es explícito a propósito.
 *
 *  El intento anterior —`items-stretch` + `h-full` + `aspect-square` en las
 *  miniaturas— se disparaba de alto: dentro del contenedor con
 *  `overflow-x-auto` la altura porcentual no resuelve (la del padre depende
 *  del contenido, o sea referencia circular), el tile cae a alto automático y
 *  entonces `aspect-square` deriva la ALTURA del ancho intrínseco de la
 *  imagen — que puede ser de 1000px. Con un número fijo las tres piezas
 *  miden igual por construcción y no hay nada que resolver. */
const CARD_H = 'h-[120px]';

export const AccionCardsRow: React.FC<{
  /** Mobile: abre el sheet único de referencias (Subidas/Productos/Modelos). */
  onOpenReferencias: () => void;
  /** Referencias ya añadidas — en mobile se muestran acá, al lado de la card. */
  referencias: ReferenciaThumb[];
  onRemoveReferencia: (id: string) => void;
}> = ({ onOpenReferencias, referencias, onRemoveReferencia }) => {
  return (
    <>
      {/* Mobile: UNA sola card que abre el sheet de referencias. Tres cards
          en una pantalla angosta obligaban a scrollear horizontalmente para
          descubrir la tercera; una sola con los tres íconos comunica lo mismo
          sin gesto oculto.

          Con referencias ya añadidas la card SE ACHICA a un cuadrado
          "Añadir más" y las miniaturas van a su derecha, en scroll
          horizontal. Así lo elegido se ve acá y no arriba del textarea. */}
      {referencias.length === 0 ? (
        <button
          type="button"
          onClick={onOpenReferencias}
          className={`md:hidden w-full max-w-[680px] ${CARD_H} rounded-2xl border-2 border-neutral-200 bg-white px-4 flex flex-col items-center justify-center gap-2.5 active:scale-[0.99] hover:border-brand-primary transition-colors`}
        >
          {/* Íconos superpuestos: se pisan un poco entre sí (-ml) para leerse
              como un grupo y no como tres botones sueltos. */}
          <span className="inline-flex items-center">
            {[
              { key: 'img', Icon: ImageIcon },
              { key: 'prod', Icon: Package },
              { key: 'model', Icon: User },
            ].map(({ key, Icon }, i) => (
              <span
                key={key}
                className={`w-10 h-10 rounded-full bg-neutral-100 ring-2 ring-white inline-flex items-center justify-center text-[#02062B73] ${
                  i > 0 ? '-ml-3' : ''
                }`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={2.25} />
              </span>
            ))}
          </span>
          <span className="text-center">
            <span className="block text-[14.5px] font-semibold text-[#02062BE6]">
              Añadí referencias
            </span>
            <span className="block text-[12.5px] text-[#3C3C43]/55 mt-0.5 leading-snug">
              producto, {palabraDeModeloActual().singular} o subí una imagen
            </span>
          </span>
        </button>
      ) : (
        // "Añadir más" NO queda fijo: es un elemento más del mismo scroll,
        // el primero. Así la tira entera se desplaza como una sola cosa y no
        // hay una columna anclada comiendo ancho fijo.
        // Full-bleed hasta los bordes de la pantalla.
        //
        // SIN `w-full` a propósito: el ancho tiene que quedar en `auto`. Con
        // un ancho explícito la caja queda sobre-restringida (ancho W + dos
        // márgenes de -20) y el navegador resuelve corriéndola a la izquierda
        // y cortándola a la derecha. En `auto`, el ancho se calcula como
        // W − (−20) − (−20) = W + 40, o sea que ocupa el padding del padre a
        // ambos lados y llega justo a los bordes.
        //
        // También necesita un padre BLOCK (por eso el wrapper es `md:flex`,
        // no `flex`): dentro de un flex centrado el margen negativo se
        // compensa solo y no bleedea nada.
        //
        // El `px-5` de adentro mantiene la primera y la última card alineadas
        // con el resto del contenido; al scrollear pasan por debajo del borde.
        <div
          className="md:hidden -mx-5 px-5 overflow-x-auto scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="flex items-center gap-2">
            {/* MISMA card que la de "Añadí referencias" (borde sólido de 2px,
                radio, íconos superpuestos) en su versión compacta: es el
                mismo objeto, no otro control. */}
            <button
              type="button"
              onClick={onOpenReferencias}
              className={`shrink-0 w-[156px] ${CARD_H} rounded-2xl border-2 border-neutral-200 bg-white px-2 flex flex-col items-center justify-center gap-2 active:scale-[0.98] hover:border-brand-primary transition-colors`}
            >
              <span className="inline-flex items-center">
                {[
                  { key: 'img', Icon: ImageIcon },
                  { key: 'prod', Icon: Package },
                  { key: 'model', Icon: User },
                ].map(({ key, Icon }, i) => (
                  <span
                    key={key}
                    className={`w-7 h-7 rounded-full bg-neutral-100 ring-2 ring-white inline-flex items-center justify-center text-[#02062B73] ${
                      i > 0 ? '-ml-2' : ''
                    }`}
                  >
                    <Icon className="w-[13px] h-[13px]" strokeWidth={2.25} />
                  </span>
                ))}
              </span>
              <span className="text-[12px] font-semibold text-[#02062BE6] leading-tight">
                Añadir más
              </span>
            </button>
            {/* Miniaturas: mismo alto que la card. Nada sobresale del tile
                (la X va adentro), así que el overflow-x no clipea nada. */}
            {referencias.map((r) => (
                <div
                  key={r.id}
                  className={`relative shrink-0 w-[120px] ${CARD_H} rounded-2xl overflow-hidden border border-black/[0.06] bg-neutral-100`}
                >
                  {r.url ? (
                    <CachedImg
                      src={r.url}
                      alt={r.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[10.5px] text-[#02062B99] px-1 text-center leading-tight">
                      {r.label}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemoveReferencia(r.id)}
                    aria-label={`Quitar ${r.label}`}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 text-white inline-flex items-center justify-center"
                  >
                    <X className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AccionCardsRow;
